import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { getSessionStorageValue, setSessionStorageValue, STORAGE_KEYS } from '../../Utils/SessionStorage';
import { getCurrentDateString } from '../../Utils/date';

const ScheduleContext = createContext();

export const ScheduleProvider = ({ children }) => {
  const [selectedDate, setSelectedDateState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.DATE) || getCurrentDateString()
  );

  const [selectedTime, setSelectedTimeState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.TIME) || null
  );

  // Keep the API flag tied to the checked slot, including across a refresh.
  const [slotPublicHoliday, setSlotPublicHolidayState] = React.useState(
    () => getSessionStorageValue(STORAGE_KEYS.SLOT_PUBLIC_HOLIDAY)
  );
  const isPublicHoliday = slotPublicHoliday?.date === selectedDate && slotPublicHoliday?.time === selectedTime
    ? slotPublicHoliday.isPublicHoliday
    : false;

  const setSlotPublicHoliday = useCallback((date, time, isPublicHoliday = false) => {
    const slot = { date, time, isPublicHoliday };
    setSlotPublicHolidayState(slot);
    setSessionStorageValue(STORAGE_KEYS.SLOT_PUBLIC_HOLIDAY, slot);
  }, []);

  const setSelectedDate = useCallback((date) => {
    setSelectedDateState(date);
    setSessionStorageValue(STORAGE_KEYS.DATE, date);
  }, []);

  const setSelectedTime = useCallback((time) => {
    setSelectedTimeState(time);
    setSlotPublicHolidayState(null);
    sessionStorage.removeItem(STORAGE_KEYS.SLOT_PUBLIC_HOLIDAY);
    if (time === null) {
      setSessionStorageValue(STORAGE_KEYS.TIME, null);
    } else {
      setSessionStorageValue(STORAGE_KEYS.TIME, time);
    }
  }, []);

  const value = useMemo(
    () => ({
      selectedDate,
      setSelectedDate,
      selectedTime,
      setSelectedTime,
      isPublicHoliday,
      setSlotPublicHoliday,
    }),
    [selectedDate, setSelectedDate, selectedTime, setSelectedTime, isPublicHoliday, setSlotPublicHoliday]
  );

  return (
    <ScheduleContext.Provider value={value}>
      {children}
    </ScheduleContext.Provider>
  );
};

export const useSchedule = () => {
  const context = useContext(ScheduleContext);
  if (!context) {
    throw new Error('useSchedule must be used within ScheduleProvider');
  }
  return context;
};

export default ScheduleContext;
